import React, { useState, useRef, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/cjs/styles/prism";
import { Link } from "react-router-dom";
import "./CompanyChatPanel.css";
import { AgentTaskViewer } from "./AgentTaskViewer";

function derivePrepSessionTitle(messages: { role: string; content: string }[], firstUserContent?: string): string {
    const firstUser = messages.find((m) => m.role === "user");
    const content = firstUser?.content || firstUserContent;
    if (content) {
        const trimmed = content.trim();
        const truncated = trimmed.slice(0, 50);
        return truncated + (trimmed.length > 50 ? "…" : "");
    }
    return "New chat";
}

export interface CandidatePrepChatPanelProps {
    darkMode: boolean;
    userId: string | null;
    sessionId?: string | null;
    fetchSession?: (sessionId: string) => Promise<{ messages: { role: string; content: string }[]; type?: string; taskId?: string } | null>;
    onSessionUpdate?: (sessionId: string, title: string) => void;
    onSaveSession?: (sessionId: string | null, data: { messages: { role: string; content: string }[]; title?: string }) => Promise<string | null>;
    onResearchStarted?: (taskId: string, title: string) => Promise<string | null>;
    onRefineResearch?: (newTaskId: string) => Promise<void>;
    onSandboxCreated?: (sessionId: string) => void;
    /** User's sandboxes (id, title) for update_practice_sandbox context */
    sandboxes?: { id: string; title: string }[];
}

interface ChatMessage {
    role: "user" | "assistant";
    content: string;
    toolCall?: string;
}

const getApiBase = () => {
    if (process.env.NODE_ENV === "production") {
        return process.env.REACT_APP_BACKEND_URL || "";
    }
    return process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";
};

const CandidatePrepChatPanel: React.FC<CandidatePrepChatPanelProps> = ({
    darkMode,
    userId,
    sessionId,
    fetchSession,
    onSessionUpdate,
    onSaveSession,
    onResearchStarted,
    onRefineResearch,
    onSandboxCreated,
    sandboxes = [],
}) => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [currentSessionId, setCurrentSessionId] = useState<string | null>(sessionId ?? null);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [currentToolCall, setCurrentToolCall] = useState<string | null>(null);
    const [lastSandboxLink, setLastSandboxLink] = useState<string | null>(null);
    const [lastCreatedSandboxId, setLastCreatedSandboxId] = useState<string | null>(null);
    const [sessionData, setSessionData] = useState<{ type?: string; taskId?: string; messages?: ChatMessage[] } | null>(null);
    const [researchTaskComplete, setResearchTaskComplete] = useState(false);
    const [refineInput, setRefineInput] = useState("");
    const [refineLoading, setRefineLoading] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const prevLoadingRef = useRef(false);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, currentToolCall]);

    useEffect(() => {
        if (prevLoadingRef.current && !isLoading) {
            inputRef.current?.focus();
        }
        prevLoadingRef.current = isLoading;
    }, [isLoading]);

    // Load session when sessionId changes; clear sandbox link when switching chats
    useEffect(() => {
        setCurrentSessionId(sessionId ?? null);
        setLastSandboxLink(null);
        setLastCreatedSandboxId(null);
        if (!sessionId || !fetchSession) {
            setMessages([]);
            setSessionData(null);
            return;
        }
        let cancelled = false;
        fetchSession(sessionId).then((data) => {
            if (!cancelled && data) {
                setSessionData({ type: data.type, taskId: data.taskId, messages: data.messages as ChatMessage[] });
                setMessages((data.messages || []) as ChatMessage[]);
            }
        });
        return () => { cancelled = true; };
    }, [sessionId, fetchSession]);

    // Poll research task status to enable refine input when complete
    useEffect(() => {
        if (!sessionData?.taskId || sessionData?.type !== "research") {
            setResearchTaskComplete(false);
            return;
        }
        const poll = async () => {
            try {
                const res = await fetch(
                    `${getApiBase()}/api/agent/tasks/${encodeURIComponent(sessionData.taskId!)}`
                );
                if (!res.ok) return;
                const data = await res.json();
                setResearchTaskComplete(data.status === "completed");
            } catch {
                // ignore
            }
        };
        poll();
        const interval = setInterval(poll, 3000);
        return () => clearInterval(interval);
    }, [sessionData?.taskId, sessionData?.type]);

    const handleRefine = async () => {
        const feedback = refineInput.trim();
        if (!feedback || !sessionData?.taskId || !onRefineResearch || !userId || refineLoading) return;

        setRefineLoading(true);
        setRefineInput("");
        try {
            const res = await fetch(`${getApiBase()}/api/agent/tasks/refine`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    taskId: sessionData.taskId,
                    feedback,
                    userId,
                }),
            });
            if (!res.ok) throw new Error("Failed to create refinement");
            const data = await res.json();
            const newTaskId = data.taskId;
            await onRefineResearch(newTaskId);
            setSessionData((prev) => (prev ? { ...prev, taskId: newTaskId } : null));
            setResearchTaskComplete(false);
        } catch (err) {
            console.error("Refine error:", err);
        } finally {
            setRefineLoading(false);
        }
    };

    const handleSend = async () => {
        const text = input.trim();
        if (!text || !userId || isLoading) return;

        const userMessage: ChatMessage = { role: "user", content: text };
        setMessages((prev) => [...prev, userMessage]);
        setInput("");
        setIsLoading(true);
        setLastSandboxLink(null);
        setTimeout(() => inputRef.current?.focus(), 0);

        try {
            const endpoint = "/api/ai/prep-agent";
            const response = await fetch(`${getApiBase()}${endpoint}`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    messages: [...messages, userMessage].map((m) => ({
                        role: m.role,
                        content: m.content,
                    })),
                    userId,
                    stream: true,
                    sandboxes: sandboxes.map((s) => ({ id: s.id, title: s.title })),
                    lastCreatedSandboxId: lastCreatedSandboxId || undefined,
                }),
            });

            if (!response.ok) {
                throw new Error("Failed to get response");
            }

            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let streamingContent = "";
            let toolsUsed: string[] = [];
            let finalResponse = "";
            let sessionId: string | null = null;
            let sandboxLink: string | null = null;

            if (reader) {
                setMessages((prev) => [...prev, { role: "assistant", content: "" }]);

                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split("\n");
                    buffer = lines.pop() || "";

                    for (const line of lines) {
                        if (line.startsWith("data: ")) {
                            const data = line.slice(6);
                            if (data === "[DONE]") continue;

                            try {
                                const parsed = JSON.parse(data);
                                if (parsed.type === "text" && parsed.content) {
                                    streamingContent += parsed.content;
                                    const contentToSet = streamingContent;
                                    setMessages((prev) => {
                                        const next = [...prev];
                                        const last = next[next.length - 1];
                                        if (last?.role === "assistant") {
                                            next[next.length - 1] = { ...last, content: contentToSet };
                                        }
                                        return next;
                                    });
                                } else if (parsed.type === "tool_call" && parsed.name) {
                                    if (!toolsUsed.includes(parsed.name)) toolsUsed.push(parsed.name);
                                    setCurrentToolCall(parsed.name);
                                } else if (parsed.type === "complete") {
                                    finalResponse = parsed.response || "";
                                    if (parsed.sessionId) {
                                        sessionId = parsed.sessionId;
                                        setLastCreatedSandboxId(parsed.sessionId);
                                        onSandboxCreated?.(parsed.sessionId);
                                    }
                                    if (parsed.sandboxLink) {
                                        sandboxLink = parsed.sandboxLink;
                                        setLastSandboxLink(parsed.sandboxLink);
                                    }
                                    if (parsed.researchStarted && parsed.taskId && onResearchStarted && userId) {
                                        const title = parsed.researchCompany
                                            ? `Research: ${parsed.researchCompany}`
                                            : derivePrepSessionTitle([...messages, userMessage], userMessage.content);
                                        onResearchStarted(parsed.taskId, title);
                                    }
                                    let content = finalResponse;
                                    if (content && toolsUsed.length > 0) {
                                        content = `Used: ${toolsUsed.map((t) => t.replace(/_/g, " ")).join(", ")}\n\n${content}`;
                                    }
                                    if (content) {
                                        streamingContent = content;
                                        setMessages((prev) => {
                                            const next = [...prev];
                                            const last = next[next.length - 1];
                                            if (last?.role === "assistant") {
                                                next[next.length - 1] = { ...last, content };
                                            } else {
                                                next.push({ role: "assistant", content });
                                            }
                                            return next;
                                        });
                                    }
                                }
                            } catch {
                                // ignore parse errors
                            }
                        }
                    }
                }

                // Save session after response completes
                const assistantContent = streamingContent || finalResponse;
                if (onSaveSession && assistantContent) {
                    const newMessages = [...messages, userMessage, { role: "assistant" as const, content: assistantContent }];
                    const title = derivePrepSessionTitle(newMessages, userMessage.content);
                    const sid = await onSaveSession(currentSessionId, { messages: newMessages, title });
                    if (sid) {
                        setCurrentSessionId(sid);
                        onSessionUpdate?.(sid, title);
                    }
                }
            }
        } catch (err) {
            setMessages((prev) => {
                const last = prev[prev.length - 1];
                if (last?.role === "assistant" && last.content === "") {
                    return [...prev.slice(0, -1), { ...last, content: "Sorry, something went wrong. Please try again." }];
                }
                return [...prev, { role: "assistant" as const, content: "Sorry, something went wrong. Please try again." }];
            });
        } finally {
            setIsLoading(false);
            setCurrentToolCall(null);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <div className={`company-chat-panel ${darkMode ? "dashboard-dark" : ""}`}>
            <div className="company-chat-header">
                <h3>Interview Prep Assistant</h3>
                <p>
                    Search for interview questions, get practice problems, create system design sandboxes, or ask for company research. I can create a sandbox for you to practice coding or system design. For company research (e.g. &quot;research Stripe&quot;), I&apos;ll start a multi-step task that takes a few minutes.
                </p>
            </div>

            {sessionData?.type === "research" && sessionData?.taskId ? (
                <div className="company-chat-research-view">
                    <AgentTaskViewer
                        taskId={sessionData.taskId}
                        darkMode={darkMode}
                    />
                    {researchTaskComplete && onRefineResearch && (
                        <div className="company-chat-refine-wrap">
                            <div className="company-chat-input-row">
                                <textarea
                                    className="company-chat-input"
                                    value={refineInput}
                                    onChange={(e) => setRefineInput(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                            e.preventDefault();
                                            handleRefine();
                                        }
                                    }}
                                    placeholder="Refine the report... e.g. add more about their tech stack"
                                    rows={1}
                                    disabled={refineLoading}
                                />
                                <button
                                    className="company-chat-send"
                                    onClick={handleRefine}
                                    disabled={refineLoading || !refineInput.trim()}
                                >
                                    {refineLoading ? "Refining…" : "Refine"}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
            <>
            <div className="company-chat-scroll-area">
                <div className="company-chat-messages">
                    {messages.length === 0 && !currentToolCall && (
                        <div className="company-chat-empty">
                            <p>Ask me for interview questions, practice problems, system design practice, or company research.</p>
                            <p>e.g. &quot;Find common Python interview questions for backend engineers&quot;</p>
                            <p>e.g. &quot;Create a practice sandbox for two sum in JavaScript&quot;</p>
                            <p>e.g. &quot;Design a URL shortener&quot;</p>
                            <p>e.g. &quot;Research Stripe for my interview&quot; (multi-step, takes a few minutes)</p>
                        </div>
                    )}
                    {messages.map((msg, i) => (
                        <div
                            key={i}
                            className={`company-chat-message company-chat-markdown ${
                                msg.role === "user" ? "user" : "assistant"
                            }`}
                        >
                            <ReactMarkdown
                                components={{
                                    code({ node, className, children, ...props }) {
                                        const match = /language-(\w+)/.exec(className || "");
                                        const content = String(children).replace(/\n$/, "");
                                        const isInline = !match && !content.includes("\n");

                                        if (isInline) {
                                            return (
                                                <code className={className} {...props}>
                                                    {children}
                                                </code>
                                            );
                                        }
                                        return (
                                            <SyntaxHighlighter
                                                style={oneDark as Record<string, React.CSSProperties>}
                                                language={match ? match[1] : "text"}
                                                PreTag="div"
                                                customStyle={{
                                                    margin: "0.5em 0",
                                                    borderRadius: "6px",
                                                    fontSize: "0.85em",
                                                    padding: "0.8em",
                                                }}
                                            >
                                                {content}
                                            </SyntaxHighlighter>
                                        );
                                    },
                                    a({ href, children, ...props }) {
                                        if (href?.startsWith("/box/")) {
                                            return (
                                                <Link to={href} {...props}>
                                                    {children}
                                                </Link>
                                            );
                                        }
                                        return (
                                            <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                                                {children}
                                            </a>
                                        );
                                    },
                                }}
                            >
                                {msg.content}
                            </ReactMarkdown>
                        </div>
                    ))}
                    {currentToolCall && (
                        <div className="company-chat-message tool-call">
                            Using: {currentToolCall.replace(/_/g, " ")}
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {lastSandboxLink && (
                <div className="company-chat-plan-actions-bar">
                    <Link
                        to={lastSandboxLink}
                        className="company-chat-send"
                        style={{ textDecoration: "none", textAlign: "center" }}
                    >
                        Open in Sandbox
                    </Link>
                    <button
                        type="button"
                        className="company-chat-plan-actions-close"
                        onClick={() => setLastSandboxLink(null)}
                        title="Dismiss"
                        aria-label="Dismiss"
                    >
                        ×
                    </button>
                </div>
            )}

            <div className="company-chat-input-wrap">
                <div className="company-chat-input-row">
                    <textarea
                        ref={inputRef}
                        className="company-chat-input"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Type a message..."
                        rows={1}
                        disabled={isLoading}
                    />
                    <button
                        className="company-chat-send"
                        onClick={handleSend}
                        disabled={isLoading || !input.trim()}
                    >
                        Send
                    </button>
                </div>
            </div>
            </>
            )}
        </div>
    );
};

export default CandidatePrepChatPanel;
